import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ArchiveRecord } from '../types/moderation';
import {
  claimSubmission,
  getSubmission,
  reRunAi,
  releaseSubmission,
} from '../lib/api';
import { ApiError } from '../lib/api';
import { useCurrentUser } from '../lib/auth';
import { useIsMobile } from '../lib/use-viewport';
import { toast } from '../lib/toast';
import StatusBadge from './StatusBadge';
import MetadataEditor from './MetadataEditor';
import MediaGallery from './MediaGallery';
import ModerationTimeline from './ModerationTimeline';
import ModerationActions from './ModerationActions';
import AiSidebarSummary from './AiSidebarSummary';
import SuggestReply from './SuggestReply';
import ContributorCard from './ContributorCard';
import ErrorState from './ErrorState';
import MobileSubmissionDetail from './MobileSubmissionDetail';
import { neighborIds } from '../lib/submission-nav';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/Tabs';
import Skeleton from './ui/Skeleton';

interface SubmissionDetailProps {
  submissionId: string;
  onBack: () => void;
}

function publicSiteUrl(wpPostId: number): string {
  // `VITE_WP_SITE_URL` is the canonical env var for this link; `VITE_PUBLIC_SITE_URL`
  // is the older name still present in some build environments, kept as a fallback.
  const base =
    (import.meta.env.VITE_WP_SITE_URL as string) ||
    (import.meta.env.VITE_PUBLIC_SITE_URL as string) ||
    '';
  if (!base) return '';
  // WordPress will follow the post-id permalink to the canonical URL.
  return `${base.replace(/\/$/, '')}/pride-history/?p=${wpPostId}`;
}

export default function SubmissionDetail({ submissionId, onBack }: SubmissionDetailProps) {
  const isMobile = useIsMobile();
  // Mobile gets a full-bleed media carousel + sticky action bar layout
  // (Phase 2). The desktop body below is left untouched.
  if (isMobile) {
    return <MobileSubmissionDetail submissionId={submissionId} onBack={onBack} />;
  }
  return <DesktopSubmissionDetail submissionId={submissionId} onBack={onBack} />;
}

function DesktopSubmissionDetail({ submissionId, onBack }: SubmissionDetailProps) {
  const [record, setRecord] = useState<ArchiveRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState('details');
  const [refreshing, setRefreshing] = useState(false);
  const [reRunBusy, setReRunBusy] = useState(false);
  const [reRunError, setReRunError] = useState<string | null>(null);
  const [forceReleaseBusy, setForceReleaseBusy] = useState(false);
  /**
   * F-107: cooldown timestamp. Set after each successful re-run; the
   * button stays disabled until 60s pass. Prevents accidental double
   * Bedrock invocations from confused volunteers.
   */
  const [reRunCooldownUntil, setReRunCooldownUntil] = useState<number>(0);
  const [, forceCooldownTick] = useState(0);
  const currentUser = useCurrentUser();

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    getSubmission(submissionId)
      .then((data) => {
        if (cancelled) return;
        setRecord(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setError((err as Error).message || 'Failed to load submission');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  // Claim-on-open / release-on-close. Both are best-effort and ignore
  // errors so a 409 (someone else already holds the claim) does not
  // prevent the moderator from viewing the record.
  useEffect(() => {
    let cancelled = false;
    claimSubmission(submissionId)
      .then((res) => {
        if (cancelled) return;
        // Refresh the record so the local view picks up our fresh claim.
        setRecord((prev) => (prev ? { ...prev, claim: res.claim } : prev));
      })
      .catch(() => {
        // Ignore: 409 means another moderator holds it (banner will
        // render once `record` arrives), other errors are non-fatal.
      });
    return () => {
      cancelled = true;
      // Fire and forget; ignore failures.
      releaseSubmission(submissionId).catch(() => undefined);
    };
  }, [submissionId]);

  // Manual refresh: same fetch path, but surface a busy state on the button
  // and don't blank the whole screen while it runs.
  const refresh = useCallback(() => {
    setRefreshing(true);
    getSubmission(submissionId)
      .then((data) => {
        setRecord(data);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError((err as Error).message || 'Failed to refresh');
      })
      .finally(() => setRefreshing(false));
  }, [submissionId]);

  // Listen for the AiSidebarSummary "View reasoning" link, which switches
  // the active tab to History so the user can see the matching AI entry.
  useEffect(() => {
    const handler = () => setTab('history');
    window.addEventListener('moderation:show-history', handler as EventListener);
    return () =>
      window.removeEventListener('moderation:show-history', handler as EventListener);
  }, []);

  const { prev, next } = useMemo(() => neighborIds(submissionId), [submissionId, record]);

  const goPrev = useCallback(() => {
    if (!prev) return;
    window.location.hash = `#/submissions/${encodeURIComponent(prev)}`;
  }, [prev]);

  const goNext = useCallback(() => {
    if (!next) return;
    window.location.hash = `#/submissions/${encodeURIComponent(next)}`;
  }, [next]);

  // Keyboard shortcuts: j = next, k = previous. Ignored while typing in
  // an input or textarea so the moderator can still type normally.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'j') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'k') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goPrev, goNext]);

  const handleForceRelease = async () => {
    setForceReleaseBusy(true);
    try {
      await releaseSubmission(submissionId);
      refresh();
    } catch {
      // Fall through; the refresh below will surface any persistent state.
    } finally {
      setForceReleaseBusy(false);
    }
  };

  const handleReRunAi = async () => {
    setReRunBusy(true);
    setReRunError(null);
    try {
      await reRunAi(submissionId);
      // ai-moderate appended a new trace, so re-fetch.
      refresh();
      toast.success('AI re-ran. Verdict updated below.');
      // F-107: arm a 60s cooldown to discourage rapid re-runs from
      // confused volunteers (each one costs Bedrock $$).
      setReRunCooldownUntil(Date.now() + 60_000);
    } catch (err) {
      const msg = (err as Error).message || 'Re-run failed';
      setReRunError(msg);
      toast.error(msg);
    } finally {
      setReRunBusy(false);
    }
  };

  // F-107: re-render once a second so the disabled button label updates
  // ("Re-run AI (45s)") and the button re-enables on its own.
  useEffect(() => {
    if (reRunCooldownUntil <= 0) return;
    const tick = window.setInterval(() => forceCooldownTick((n) => n + 1), 1000);
    return () => window.clearInterval(tick);
  }, [reRunCooldownUntil]);

  const reRunCooldownSeconds = Math.max(
    0,
    Math.ceil((reRunCooldownUntil - Date.now()) / 1000),
  );

  const navigateById = (id: string) => {
    window.location.hash = `#/submissions/${encodeURIComponent(id)}`;
  };

  const headerNav = (
    <div className="submission-detail__nav">
      <a href="#/submissions" className="back-link" onClick={(e) => { e.preventDefault(); onBack(); }}>
        &larr; Back to list
      </a>
      <button
        type="button"
        className="btn btn-secondary submission-detail__refresh"
        onClick={refresh}
        disabled={refreshing || loading}
        data-testid="detail-refresh"
        aria-label="Refresh submission"
      >
        {refreshing ? 'Refreshing…' : 'Refresh'}
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={goPrev}
        disabled={!prev}
        title="Previous (k)"
        data-testid="detail-prev"
      >
        ← Prev <kbd>k</kbd>
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={goNext}
        disabled={!next}
        title="Next (j)"
        data-testid="detail-next"
      >
        Next <kbd>j</kbd> →
      </button>
    </div>
  );

  if (loading) {
    // F-008: render a full-shape skeleton so the moderator perceives
    // the layout immediately. aria-busy lives on the host.
    return (
      <div className="submission-detail" aria-busy="true" data-testid="detail-loading">
        {headerNav}
        <header className="submission-detail__header">
          <div>
            <Skeleton variant="title" width="60%" />
            <div className="submission-detail__chips" aria-hidden="true">
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={80} height={20} />
            </div>
          </div>
        </header>
        <div className="submission-detail__layout submission-detail__layout--loading">
          <div className="submission-detail__main">
            <Skeleton variant="block" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="65%" />
            <Skeleton variant="block" />
          </div>
          <div className="submission-detail__side">
            <Skeleton variant="card" />
            <Skeleton variant="card" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="submission-detail">
        {headerNav}
        <div className="error-state" role="alert" data-testid="detail-not-found">
          Submission not found.
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="submission-detail">
        {headerNav}
        <ErrorState
          message="We could not load this submission."
          detail={error}
          onRetry={() => load()}
          testId="detail-error"
        />
      </div>
    );
  }

  if (!record) return null;

  const publicUrl =
    record.moderationStatus === 'approved' && record.wpPostId
      ? publicSiteUrl(record.wpPostId)
      : '';

  const role = currentUser?.role ?? 'volunteer';
  const isLeadOrAdmin = role === 'lead_moderator' || role === 'admin';
  const claim = record.claim;
  const claimedByOther =
    claim && currentUser && claim.userId !== currentUser.sub ? claim : null;

  return (
    <div className="submission-detail">
      {headerNav}
      {claimedByOther && (
        <div
          id="claim-banner-id"
          className="claim-banner"
          role="status"
          data-testid="claim-banner"
        >
          <span>
            Currently being reviewed by <strong>{claimedByOther.userEmail}</strong>.
            Action buttons are disabled until they release the claim.
          </span>
          {isLeadOrAdmin && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleForceRelease}
              disabled={forceReleaseBusy}
              data-testid="force-release"
            >
              {forceReleaseBusy ? 'Releasing…' : 'Force release'}
            </button>
          )}
        </div>
      )}
      {record.moderationStatus === 'awaiting_peer_review' && record.peerReview && (
        <div
          id="peer-review-banner-id"
          className="peer-review-banner"
          role="status"
          data-testid="peer-review-banner"
        >
          Awaiting peer review. First vote (
          <strong>{record.peerReview.firstReview.action}</strong>) by{' '}
          <strong>{record.peerReview.firstReview.userEmail}</strong>. A second
          decision by a lead moderator or admin is required to finalise.
        </div>
      )}
      <header className="submission-detail__header">
        <div>
          <h1>{record.title}</h1>
          <div className="submission-detail__chips">
            <StatusBadge status={record.moderationStatus} />
            {record.mediaType && (
              <span className="chip chip--media-type" data-testid="chip-mediaType">
                {record.mediaType}
              </span>
            )}
            {record.submissionMode && (
              <span className="chip chip--mode" data-testid="chip-submissionMode">
                {record.submissionMode}
              </span>
            )}
            {record.pipeline?.status && record.pipeline.status !== 'complete' && (
              <span
                className={`chip chip--pipeline pipeline-status--${record.pipeline.status}`}
                data-testid="chip-pipelineStatus"
              >
                pipeline: {record.pipeline.status}
              </span>
            )}
          </div>
        </div>
      </header>
      <div className="submission-detail__layout">
        <div className="submission-detail__main">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList aria-label="Submission sections">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="media">Media</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="details">
              <MetadataEditor record={record} onSaved={setRecord} section="metadata" />
            </TabsContent>
            <TabsContent value="media">
              <MediaGallery record={record} onUpdated={setRecord} />
            </TabsContent>
            <TabsContent value="history">
              <ModerationTimeline
                history={record.moderationHistory}
                aiAnalysis={record.aiAnalysis}
              />
            </TabsContent>
            <TabsContent value="notes">
              <MetadataEditor record={record} onSaved={setRecord} section="notes" />
            </TabsContent>
          </Tabs>
        </div>
        <div className="submission-detail__side">
          {/* F-101: pin the AI verdict and Actions to the top of the
              sidebar so the moderator sees verdict and decision verbs
              first. Contributor / reply / re-run move below. */}
          <AiSidebarSummary record={record} />
          <ModerationActions
            record={record}
            onModerated={load}
            disabled={Boolean(claimedByOther)}
          />
          <div className="submission-detail__re-run">
            <button
              type="button"
              className="btn btn-secondary btn-icon submission-detail__re-run-btn"
              onClick={handleReRunAi}
              disabled={reRunBusy || reRunCooldownSeconds > 0}
              data-testid="re-run-ai"
              aria-label={reRunBusy ? 'Re-running AI' : 'Re-run AI'}
            >
              {reRunBusy ? (
                'Re-running AI…'
              ) : reRunCooldownSeconds > 0 ? (
                <>Re-run AI ({reRunCooldownSeconds}s)</>
              ) : (
                <>
                  Re-run AI
                  {record.aiAnalysis && record.aiAnalysis.length > 0 && (
                    <span
                      className="submission-detail__re-run-count"
                      aria-label={`${record.aiAnalysis.length} prior runs`}
                    >
                      ({record.aiAnalysis.length} prior)
                    </span>
                  )}
                </>
              )}
            </button>
            {reRunError && (
              <div className="field-error" role="alert" data-testid="re-run-error">
                {reRunError}
              </div>
            )}
          </div>
          <ContributorCard record={record} onSelect={navigateById} />
          <SuggestReply record={record} />
          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary submission-detail__public-link"
              data-testid="view-on-public-site"
            >
              View on public site ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
